import { useEffect, useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { SignPad } from '../../components/SignPad.jsx';
import { CommandeClient } from './CommandeClient.jsx';
import { FeuilleDocument } from './FeuilleDocument.jsx';
import { DemandeNom, Transmettre } from './PersonnesPage.jsx';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { estInstallee } from '../../pwa/installation';
import { utiliseManifeste } from '../../pwa/manifeste';
import { iconeAttestation } from '../../lib/attestations';
import { tr } from '../../lib/i18n';
import { dateSuisse, montantSuisse, quantiteLisible } from '../../lib/lien-public';
import { termesOffre } from '../../lib/terminologie';
import { activeLaTraduction } from '../../lib/traduction-ecran';
import { C } from '../../theme/palette';
import { NOM_LOGICIEL } from '../../lib/marque';

const TITRES = {
  DEVIS: 'Devis',
  FACTURE: 'Facture',
  LIVRAISON: 'Bon de livraison',
  CHANTIER: 'Suivi de chantier',
  COMMANDE: 'Commande en ligne',
};

/**
 * Les formes du nom de la pièce, tel que l'entreprise l'a réglé.
 *
 * La page disait « Devis » quand le PDF joint disait « Offre » : le serveur
 * envoie le mot choisi dans Réglages → Documents (`nomPiece`), et la page
 * l'accorde — « l’offre », « le devis ».
 *
 * @param {object} page la page rendue par le serveur
 * @returns {ReturnType<typeof termesOffre>} les formes du mot
 */
const termesDePage = (page) =>
  termesOffre({ motOffre: String(page?.nomPiece || TITRES.DEVIS).toLowerCase() });

/** Étiquette d'une photo de chantier selon sa catégorie. */
const CATEGORIES_PHOTO = {
  avant: 'Avant',
  pendant: 'Pendant',
  apres: 'Après',
  probleme: 'Point relevé',
};

/** Couleur de l'état : le vert rassure, l'ambre attend, le rouge alerte. */
const couleurEtat = (statut) => {
  if (/Réglée$|Accepté|Livrée$|^Livré$|Terminé/.test(statut)) return [C.green, C.greenL];
  if (/Échue|Refusé|Expiré|rupture|Suspendu/.test(statut)) return [C.red, C.redL];
  return [C.amber, C.amberL];
};

const carte = {
  background: '#fff',
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 18,
  marginBottom: 14,
};

const etiquette = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

/**
 * « 📎 Documents de l'entreprise » : les attestations jointes à l'offre —
 * assurance RC, extrait du registre du commerce… —, chacune à ouvrir ou à
 * garder. Leur nom est celui que l'entreprise leur a donné : il ne se
 * traduit pas.
 *
 * @param {object} props propriétés du composant
 * @param {{libelle: string, categorie: ?string, typeMedia: ?string, adresse: string}[]} props.documents
 *   les attestations jointes
 * @returns {JSX.Element} la section
 */
export function DocumentsEntreprise({ documents }) {
  return (
    <section style={carte} aria-label={tr('Documents de l’entreprise')}>
      <div style={etiquette}>📎 {tr('Documents de l’entreprise')}</div>
      {documents.map((d, i) => (
        <a
          key={d.adresse || i}
          href={pagesPubliquesApi.adresseDuDocumentJoint(d.adresse)}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 0',
            borderTop: i ? '1px solid ' + C.border : 'none',
            color: C.ink,
            textDecoration: 'none',
            fontSize: 14,
          }}
        >
          <span style={{ fontSize: 18 }}>{iconeAttestation(d.categorie)}</span>
          <span
            translate="no"
            style={{ flex: 1, minWidth: 0, fontWeight: 700, overflowWrap: 'anywhere' }}
          >
            {d.libelle}
          </span>
          <span style={{ fontSize: 12, fontWeight: 800, color: C.teal, whiteSpace: 'nowrap' }}>
            {tr('Ouvrir ›')}
          </span>
        </a>
      ))}
    </section>
  );
}

function Etat({ statut }) {
  const [texte, fond] = couleurEtat(statut || '');
  return (
    <span
      style={{
        display: 'inline-block',
        background: fond,
        color: texte,
        fontSize: 12,
        fontWeight: 800,
        borderRadius: 99,
        padding: '5px 12px',
      }}
    >
      {statut}
    </span>
  );
}

function Cadre({ children }) {
  return (
    <div style={{ minHeight: '100svh', background: C.bg, color: C.ink, padding: '22px 14px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

/**
 * « ← Retour » en haut de la page du client.
 *
 * <p>Ouverte depuis l'application installée sur l'iPhone, la page n'a pas de
 * barre du navigateur : on y restait bloqué, sans moyen de revenir. Le bouton
 * paraît quand il y a une page d'où l'on vient, ou dans l'application
 * installée ; sans rien derrière, il ramène à l'accueil de SwissTech. Ouverte
 * dans un nouvel onglet (WhatsApp, e-mail), la page n'en a pas besoin : le
 * navigateur a ses propres boutons.
 *
 * @returns {JSX.Element|null} le bouton
 */
function BoutonRetour() {
  const [possible] = useState(() => {
    try {
      return window.history.length > 1 || estInstallee();
    } catch {
      return false;
    }
  });
  if (!possible) return null;
  return (
    <button
      type="button"
      onClick={() => {
        if (window.history.length > 1) window.history.back();
        else window.location.assign('/');
      }}
      style={{
        background: C.white,
        border: '1px solid ' + C.border,
        borderRadius: 10,
        padding: '8px 14px',
        fontSize: 14,
        fontWeight: 800,
        color: C.navy,
        cursor: 'pointer',
        marginBottom: 12,
      }}
    >
      ← Retour
    </button>
  );
}

const bouton = {
  display: 'block',
  width: '100%',
  textAlign: 'center',
  background: C.navy,
  color: '#fff',
  fontWeight: 800,
  fontSize: 15,
  borderRadius: 12,
  padding: '14px 16px',
  textDecoration: 'none',
  border: 'none',
  cursor: 'pointer',
  marginBottom: 14,
};

/**
 * L'offre s'accepte ici même : le client écrit son nom, signe au doigt et
 * confirme. C'est l'acceptation que l'entreprise recueille sur sa tablette,
 * faite à distance ; le serveur en tire les mêmes suites.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @param {object} props.page la page de l'offre
 * @param {(page: object) => void} props.onAcceptee reçoit la page une fois l'offre acceptée
 * @returns {JSX.Element} le formulaire d'acceptation
 */
function AcceptationOffre({ jeton, page, onAcceptee }) {
  const termes = termesDePage(page);
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState(page.client || '');
  const [signature, setSignature] = useState('');
  const [accord, setAccord] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  // Dernière question avant d'accepter : un client a accepté sans le vouloir.
  const [aConfirmer, setAConfirmer] = useState(false);
  const acompte = page.offre?.montantAcompte;
  const total =
    page.totaux?.ttc != null
      ? `${page.totaux?.devise || 'CHF'} ${montantSuisse(page.totaux.ttc)}`
      : null;

  if (!ouvert) {
    return (
      <button
        type="button"
        style={{ ...bouton, background: C.green }}
        onClick={() => setOuvert(true)}
      >
        {termes.feminin ? '✍️ Accepter l’offre' : '✍️ Accepter le devis'}
      </button>
    );
  }

  const pret = nom.trim() !== '' && signature !== '' && accord && !envoi;
  const accepter = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      const relue = await pagesPubliquesApi.accepte(jeton, { signataire: nom.trim(), signature });
      onAcceptee(relue);
    } catch (e) {
      setErreur(e?.message || 'L’acceptation n’a pas pu être enregistrée. Réessayez.');
      setEnvoi(false);
    }
  };

  return (
    <section style={carte} aria-label={termes.feminin ? 'Accepter l’offre' : 'Accepter le devis'}>
      <div style={etiquette}>{termes.feminin ? 'Accepter l’offre' : 'Accepter le devis'}</div>
      <p style={{ fontSize: 14, color: C.grey, margin: '0 0 12px', lineHeight: 1.5 }}>
        {termes.feminin
          ? `Votre signature vaut acceptation de l’offre ${page.numero} et de ses conditions`
          : `Votre signature vaut acceptation du devis ${page.numero} et de ses conditions`}
        {acompte != null
          ? `, dont un acompte de ${page.totaux?.devise || 'CHF'} ${montantSuisse(acompte)} à la signature`
          : ''}
        .
      </p>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        Votre nom
        <input
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="Prénom et nom"
          maxLength={160}
          autoComplete="name"
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            marginTop: 6,
            padding: '12px 14px',
            fontSize: 15,
            border: '1px solid ' + C.border,
            borderRadius: 10,
          }}
        />
      </label>
      <div style={{ fontSize: 13, fontWeight: 700, margin: '12px 0 6px' }}>Votre signature</div>
      <SignPad value={signature} onChange={setSignature} color={C.navy} />
      <label
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          fontSize: 14,
          lineHeight: 1.5,
          margin: '12px 0 14px',
        }}
      >
        <input
          type="checkbox"
          checked={accord}
          onChange={(e) => setAccord(e.target.checked)}
          style={{ marginTop: 4, width: 18, height: 18 }}
        />
        <span>
          {termes.feminin
            ? 'J’accepte l’offre et ses conditions.'
            : 'J’accepte le devis et ses conditions.'}
        </span>
      </label>
      {erreur && (
        <div role="alert" style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>
          {erreur}
        </div>
      )}
      {aConfirmer ? (
        <div
          role="alertdialog"
          aria-label="Confirmer l’acceptation"
          style={{
            border: '2px solid ' + C.green,
            borderRadius: 12,
            padding: 14,
            background: '#F0FDF4',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Êtes-vous sûr ?</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
            {total
              ? termes.feminin
                ? `Vous acceptez l’offre ${page.numero} pour ${total}.`
                : `Vous acceptez le devis ${page.numero} pour ${total}.`
              : termes.feminin
                ? `Vous acceptez l’offre ${page.numero}.`
                : `Vous acceptez le devis ${page.numero}.`}
          </div>
          {/* La signature, le nom et la case restent modifiables au-dessus :
              effacés entre-temps, l'acceptation n'est plus possible. */}
          <button
            type="button"
            disabled={!pret}
            onClick={accepter}
            style={{ ...bouton, background: pret ? C.green : C.muted, marginBottom: 8 }}
          >
            {envoi ? 'Enregistrement…' : 'Oui, j’accepte'}
          </button>
          <button
            type="button"
            disabled={envoi}
            onClick={() => setAConfirmer(false)}
            style={{
              ...bouton,
              background: '#fff',
              color: C.grey,
              border: '1px solid ' + C.border,
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            Non, revenir
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={!pret}
          onClick={() => setAConfirmer(true)}
          style={{ ...bouton, background: pret ? C.green : C.muted, marginBottom: 0 }}
        >
          ✓ Je signe et j’accepte
        </button>
      )}
    </section>
  );
}

/**
 * Le client peut aussi dire non. La page ne le permettait pas : une offre
 * refusée restait « en attente » au bureau, qui relançait un client ayant déjà
 * choisi ailleurs. Le motif est facultatif ; le refus se confirme en deux
 * gestes, pour qu'un doigt qui glisse ne décide de rien.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @param {object} props.page la page de l'offre
 * @param {(page: object) => void} props.onRefusee reçoit la page une fois l'offre refusée
 * @returns {JSX.Element} le bouton, puis le formulaire de refus
 */
function RefusOffre({ jeton, page, onRefusee }) {
  const termes = termesDePage(page);
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  // Dernière question avant de refuser, comme pour accepter.
  const [aConfirmer, setAConfirmer] = useState(false);

  if (!ouvert) {
    return (
      <button
        type="button"
        style={{
          ...bouton,
          background: '#fff',
          color: C.red,
          border: '1px solid ' + C.red,
          fontSize: 14,
        }}
        onClick={() => setOuvert(true)}
      >
        {termes.feminin ? 'Refuser l’offre' : 'Refuser le devis'}
      </button>
    );
  }

  const refuser = async () => {
    setEnvoi(true);
    setErreur('');
    try {
      const relue = await pagesPubliquesApi.refuse(jeton, { motif: motif.trim() });
      onRefusee(relue);
    } catch (e) {
      setErreur(e?.message || 'Le refus n’a pas pu être enregistré. Réessayez.');
      setEnvoi(false);
    }
  };

  return (
    <section style={carte} aria-label={termes.feminin ? 'Refuser l’offre' : 'Refuser le devis'}>
      <div style={etiquette}>{termes.feminin ? 'Refuser l’offre' : 'Refuser le devis'}</div>
      <p style={{ fontSize: 14, color: C.grey, margin: '0 0 12px', lineHeight: 1.5 }}>
        Vous pouvez nous dire pourquoi, si vous le souhaitez : cela nous aide à vous proposer mieux.
      </p>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        Motif (facultatif)
        <textarea
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder="Trop cher, délai trop long, autre choix…"
          maxLength={500}
          rows={3}
          style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            marginTop: 6,
            padding: '12px 14px',
            fontSize: 15,
            fontFamily: 'inherit',
            border: '1px solid ' + C.border,
            borderRadius: 10,
            resize: 'vertical',
          }}
        />
      </label>
      {erreur && (
        <div role="alert" style={{ color: C.red, fontSize: 13, margin: '12px 0' }}>
          {erreur}
        </div>
      )}
      {aConfirmer ? (
        <div
          role="alertdialog"
          aria-label="Confirmer le refus"
          style={{
            border: '2px solid ' + C.red,
            borderRadius: 12,
            padding: 14,
            background: '#FEF2F2',
            marginTop: 14,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Êtes-vous sûr ?</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
            {termes.feminin
              ? `Vous refusez l’offre ${page.numero}.`
              : `Vous refusez le devis ${page.numero}.`}
          </div>
          <button
            type="button"
            disabled={envoi}
            onClick={refuser}
            style={{ ...bouton, background: envoi ? C.muted : C.red, marginBottom: 8 }}
          >
            {envoi ? 'Enregistrement…' : 'Oui, je refuse'}
          </button>
          <button
            type="button"
            disabled={envoi}
            onClick={() => setAConfirmer(false)}
            style={{
              ...bouton,
              background: '#fff',
              color: C.grey,
              border: '1px solid ' + C.border,
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            Non, revenir
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
          <button
            type="button"
            onClick={() => setOuvert(false)}
            disabled={envoi}
            style={{
              ...bouton,
              background: '#fff',
              color: C.grey,
              border: '1px solid ' + C.border,
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => setAConfirmer(true)}
            disabled={envoi}
            style={{
              ...bouton,
              background: envoi ? C.muted : C.red,
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            Confirmer le refus
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Suivi d'un chantier : avancement, dates, postes, puis les photos partagées,
 * classées par poste comme dans l'onglet « Portail » de l'entreprise.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.chantier suivi rendu par la page
 * @returns {JSX.Element} les sections du chantier
 */
function SuiviChantier({ chantier }) {
  const avancement = Math.max(0, Math.min(100, Number(chantier.avancement) || 0));
  const photos = chantier.photos || [];
  const postes = [...new Set(photos.map((p) => p.poste || 'Général'))];
  return (
    <>
      <section style={carte} aria-label="Avancement">
        <div style={etiquette}>Avancement</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 30, fontWeight: 900, color: C.teal }}>{avancement}%</span>
          <span style={{ fontSize: 13, color: C.grey }}>des travaux réalisés</span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={avancement}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{ height: 10, background: C.border, borderRadius: 99, marginTop: 10 }}
        >
          <div
            style={{ width: avancement + '%', height: 10, background: C.teal, borderRadius: 99 }}
          />
        </div>
        <div style={{ fontSize: 13, color: C.grey, marginTop: 12, lineHeight: 1.7 }}>
          {chantier.adresse && <div>📍 {chantier.adresse}</div>}
          {(chantier.debut || chantier.fin) && (
            <div>
              📅 {chantier.debut ? 'Début le ' + dateSuisse(chantier.debut) : ''}
              {chantier.debut && chantier.fin ? ' · ' : ''}
              {chantier.fin ? 'Fin prévue le ' + dateSuisse(chantier.fin) : ''}
            </div>
          )}
        </div>
      </section>

      {(chantier.postes || []).length > 0 && (
        <section style={carte} aria-label="Postes de travail">
          <div style={etiquette}>Postes de travail</div>
          <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 14, lineHeight: 1.8 }}>
            {chantier.postes.map((poste, i) => (
              <li key={i}>{poste}</li>
            ))}
          </ul>
        </section>
      )}

      <section style={carte} aria-label="Photos du chantier">
        <div style={etiquette}>Photos du chantier</div>
        {photos.length === 0 ? (
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            Aucune photo pour le moment : elles apparaîtront ici au fil des travaux.
          </p>
        ) : (
          postes.map((poste) => (
            <div key={poste} style={{ marginBottom: 12 }}>
              <h2 style={{ fontSize: 14, fontWeight: 800, margin: '0 0 8px' }}>📌 {poste}</h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 10,
                }}
              >
                {photos
                  .filter((p) => (p.poste || 'Général') === poste)
                  .map((p, i) => (
                    <figure
                      key={i}
                      style={{
                        margin: 0,
                        border: '1px solid ' + C.border,
                        borderRadius: 10,
                        overflow: 'hidden',
                      }}
                    >
                      <a
                        href={pagesPubliquesApi.adresseDeLaPhoto(p.image)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={pagesPubliquesApi.adresseDeLaPhoto(p.image)}
                          alt={p.legende || p.note || 'Photo du chantier'}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: 130,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </a>
                      <figcaption style={{ padding: '6px 8px', fontSize: 12, lineHeight: 1.5 }}>
                        {CATEGORIES_PHOTO[p.categorie] && (
                          <span style={{ fontWeight: 800, color: C.teal }}>
                            {CATEGORIES_PHOTO[p.categorie]}
                            {p.date ? ' · ' : ''}
                          </span>
                        )}
                        {p.date ? (
                          <span style={{ color: C.grey }}>
                            {dateSuisse(p.date) + (p.heure ? ' · ' + p.heure : '')}
                          </span>
                        ) : null}
                        {p.note ? <div style={{ color: C.ink }}>{p.note}</div> : null}
                      </figcaption>
                    </figure>
                  ))}
              </div>
            </div>
          ))
        )}
      </section>
    </>
  );
}

/**
 * « Partager avec mon équipe » : le client du magasin transmet son lien de
 * commande à ses ouvriers, qui commandent le matériel du chantier à ses prix
 * et l'ajoutent à leur tour à leur écran d'accueil.
 *
 * @param {object} props propriétés
 * @param {string} [props.societe] nom du magasin
 * @returns {import('react').ReactElement} le bouton
 */
function PartageEquipe({ societe }) {
  const [copie, setCopie] = useState(false);
  const url =
    typeof window === 'undefined' ? '' : window.location.origin + window.location.pathname;
  const texte =
    'Lien de commande ' +
    (societe || 'du magasin') +
    ' : ' +
    url +
    '\nOuvrez-le, puis ajoutez-le à votre écran d’accueil : vous commanderez le matériel en un geste.';
  const partage = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Commandes ' + (societe || ''), text: texte });
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(true);
    } catch {
      window.open('https://wa.me/?text=' + encodeURIComponent(texte), '_blank', 'noopener');
    }
  };
  return (
    <div style={{ display: 'flex', gap: 8, margin: '0 0 12px' }}>
      <button
        type="button"
        onClick={partage}
        style={{
          flex: 1,
          border: '1px solid ' + C.border,
          borderRadius: 12,
          padding: '11px 10px',
          background: '#fff',
          color: C.ink,
          fontSize: 13,
          fontWeight: 800,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        👷 {copie ? '✓ Lien copié — collez-le à votre équipe' : 'Partager avec mon équipe'}
      </button>
      <a
        href={'https://wa.me/?text=' + encodeURIComponent(texte)}
        target="_blank"
        rel="noreferrer"
        aria-label="Envoyer par WhatsApp"
        style={{
          border: '1px solid ' + C.border,
          borderRadius: 12,
          padding: '11px 12px',
          background: '#fff',
          fontSize: 16,
          textDecoration: 'none',
        }}
      >
        💬
      </a>
    </div>
  );
}

/**
 * Page qu'un client ouvre par le lien qu'on lui a remis.
 *
 * <p>Il n'a ni compte ni application : la page se suffit, et ne montre que sa
 * pièce. Elle se lit d'abord sur un téléphone — c'est là que le lien arrive,
 * par message ou par courriel.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @returns {JSX.Element} la page
 */
export function PagePublique({ jeton }) {
  const [page, setPage] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  // Vrai quand c'est ici, à l'instant, que le client a accepté : la page le
  // remercie, puis montre l'offre acceptée comme à toute consultation suivante.
  const [vientDAccepter, setVientDAccepter] = useState(false);
  // De même pour le refus : un mot de remerciement, puis l'état de la pièce.
  const [vientDeRefuser, setVientDeRefuser] = useState(false);
  // Le document lui-même — le même que le PDF, à la taille de l'écran — est
  // affiché : le détail en cartes n'est plus qu'un repli s'il ne vient pas.
  const [feuillePrete, setFeuillePrete] = useState(false);
  // La personne qui ouvre la page : son nom, ou s'il faut le lui demander.
  const [visiteur, setVisiteur] = useState(null);

  // Le client agrandit le document avec deux doigts, comme tout PDF : la page
  // de l'application l'interdit (maximum-scale=1), pas la sienne.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const avant = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
    return () => meta.setAttribute('content', avant);
  }, []);

  useEffect(() => {
    let annule = false;
    // Arrivé par une transmission faite depuis la page (« ?de=… ») : le
    // bureau saura que c'est la personne à qui elle était adressée.
    // Arrivé par le bouton du PDF (« ?via=pdf ») : le bureau le voit aussi.
    let transmis = null;
    let via = null;
    try {
      const params = new URLSearchParams(window.location.search);
      transmis = params.get('de');
      via = params.get('via');
    } catch {
      transmis = null;
      via = null;
    }
    pagesPubliquesApi
      .page(jeton, transmis, via)
      .then((p) => {
        if (!annule) setPage(p);
      })
      .catch(() => {
        if (!annule) setIntrouvable(true);
      });
    return () => {
      annule = true;
    };
  }, [jeton]);

  // Offre ou facture : qui l'ouvre — son nom, ou s'il faut le lui demander.
  // Un échec ne prive jamais le client de sa pièce.
  const pieceAvecSuivi = page?.type === 'DEVIS' || page?.type === 'FACTURE';
  useEffect(() => {
    if (!pieceAvecSuivi) return undefined;
    let annule = false;
    Promise.resolve()
      .then(() => pagesPubliquesApi.visiteur(jeton))
      .then((v) => {
        if (!annule && v) setVisiteur(v);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [pieceAvecSuivi, jeton]);

  // Capture d'écran de la pièce : seule l'application SwissTech la voit
  // (iPhone ; Android 14 et plus), et lance cet événement. Un navigateur ne
  // le lance jamais : rien ne part.
  useEffect(() => {
    if (!pieceAvecSuivi) return undefined;
    const surCapture = () => {
      Promise.resolve()
        .then(() => pagesPubliquesApi.capture(jeton))
        .catch(() => {});
    };
    window.addEventListener('swisstechCapture', surCapture);
    return () => window.removeEventListener('swisstechCapture', surCapture);
  }, [pieceAvecSuivi, jeton]);

  useEffect(() => {
    if (page) {
      const nom = page.type === 'DEVIS' ? termesDePage(page).Mot : TITRES[page.type] || 'Document';
      document.title = `${nom} ${page.numero || ''}`.trim();
      // Installée sur l'écran d'accueil, l'icône rouvre cette page — la
      // commande au magasin, le suivi du chantier — et non l'écran de
      // connexion de l'application, que le client n'a pas.
      utiliseManifeste(`/api/public/manifeste/page/${jeton}`, {
        titre: page.societe?.nom || NOM_LOGICIEL,
        depart: `/p/${jeton}`,
      });
    }
  }, [page, jeton]);

  // La page du client parle la langue de l'entreprise, comme ses documents :
  // une entreprise de Riyad n'envoie pas une page en français.
  const langueSociete = page?.societe?.langue || 'fr';
  useEffect(() => {
    activeLaTraduction(langueSociete).catch(() => {});
  }, [langueSociete]);

  if (introuvable) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🔗</div>
          <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Ce lien n’est plus valable</h1>
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            Il a peut-être été remplacé ou retiré. Demandez-en un nouveau à l’entreprise qui vous
            l’a envoyé.
          </p>
        </div>
      </Cadre>
    );
  }

  if (!page) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', color: C.muted, fontSize: 14 }} role="status">
          Chargement…
        </div>
      </Cadre>
    );
  }

  const { societe, totaux, paiement, livraison, offre, chantier } = page;
  const estDevis = page.type === 'DEVIS';
  const termes = termesDePage(page);
  const titre = estDevis ? termes.Mot : TITRES[page.type] || 'Document';
  // Le suivi d'un chantier se présente par son nom : le client connaît
  // « Rénovation Carouge », pas la référence interne du chantier.
  const estChantier = page.type === 'CHANTIER';
  // Le lien de commande d'un client n'ouvre pas une pièce mais son catalogue.
  const estCommande = page.type === 'COMMANDE';

  const blocPaiement = paiement && (
    <section style={carte}>
      <div style={etiquette}>
        {page.type !== 'DEVIS'
          ? 'Pour régler'
          : offre?.acceptable
            ? 'Pour régler l’acompte, à la signature'
            : 'Pour régler l’acompte'}
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Le QR-facture est suisse : hors de Suisse, la page donne le
                compte et la référence d'un virement. */}
        {paiement.qr && (
          <img
            src={paiement.qr}
            alt="QR-facture à scanner avec votre application bancaire"
            width={150}
            height={150}
            style={{ border: '1px solid ' + C.border, borderRadius: 8 }}
          />
        )}
        <div style={{ fontSize: 13, lineHeight: 1.7, minWidth: 0, flex: 1 }}>
          <div style={{ color: C.grey }}>
            {paiement.qr
              ? 'Scannez ce code avec votre application bancaire.'
              : 'Faites un virement avec ces coordonnées, en indiquant la référence.'}
          </div>
          <div style={{ marginTop: 8 }}>
            <b>
              {paiement.devise} {montantSuisse(paiement.montant)}
            </b>
          </div>
          <div>{paiement.beneficiaire}</div>
          <div style={{ overflowWrap: 'anywhere' }}>{paiement.iban}</div>
          <div style={{ color: C.grey, overflowWrap: 'anywhere' }}>
            Référence : {paiement.reference}
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <Cadre>
      {vientDAccepter && (
        <section
          role="status"
          style={{
            ...carte,
            background: C.greenL,
            border: '1px solid ' + C.green,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 34 }}>✓</div>
          <h2 style={{ fontSize: 18, margin: '6px 0', color: C.green }}>
            {termes.feminin ? 'Merci, votre offre est acceptée' : 'Merci, votre devis est accepté'}
          </h2>
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            {societe?.nom} en est informée.
            {paiement
              ? ' Vous pouvez régler l’acompte ci-dessous avec votre application bancaire.'
              : ''}
          </p>
        </section>
      )}
      {vientDeRefuser && (
        <section
          role="status"
          style={{
            ...carte,
            background: C.redL,
            border: '1px solid ' + C.red,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 34 }}>✕</div>
          <h2 style={{ fontSize: 18, margin: '6px 0', color: C.red }}>
            {termes.feminin ? 'Vous avez refusé cette offre' : 'Vous avez refusé ce devis'}
          </h2>
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            {societe?.nom} en est informée. Merci de nous avoir répondu.
          </p>
        </section>
      )}
      {/* Nom et adresse de l'entreprise : jamais traduits (« Rue du Lac 12 »
          reste l'adresse, même si un exemple de l'interface porte ce texte). */}
      <BoutonRetour />
      <header translate="no" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>{societe?.nom}</div>
        <div style={{ fontSize: 12, color: C.grey, lineHeight: 1.5 }}>
          {[societe?.adresse, societe?.npa].filter(Boolean).join(' · ')}
        </div>
      </header>

      {/* Une offre signée ou refusée porte déjà le nom de qui a répondu :
          on ne lui redemande pas qui il est. */}
      {visiteur?.demandeNom && !offre?.dateSignature && !offre?.refuseLe && (
        <DemandeNom
          jeton={jeton}
          client={visiteur.client}
          onFin={(nom) => setVisiteur({ demandeNom: false, nom })}
        />
      )}

      {(estCommande || estChantier) && (
        <GuideInstallation
          vous
          replie
          nom={societe?.nom || NOM_LOGICIEL}
          apres={
            estCommande
              ? 'Vous commanderez en un geste, à vos prix.'
              : 'Vous suivrez votre chantier en un geste.'
          }
        />
      )}
      {estCommande && <PartageEquipe societe={societe?.nom} />}

      {estCommande && <CommandeClient jeton={jeton} page={page} />}

      {!estCommande && (
        <section style={carte}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h1 style={{ fontSize: 22, margin: 0, fontWeight: 900 }}>
                {estChantier ? page.objet || titre : titre + ' ' + (page.numero || '')}
              </h1>
              <div style={{ fontSize: 13, color: C.grey, marginTop: 4 }}>
                {(estChantier
                  ? [titre, page.numero, page.client]
                  : [page.client, dateSuisse(page.date)]
                )
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <Etat statut={page.statut} />
          </div>
          {page.objet && !estChantier && (
            <p style={{ fontSize: 14, margin: '12px 0 0', lineHeight: 1.5 }}>{page.objet}</p>
          )}
          {page.echeance && (
            <div style={{ fontSize: 13, color: C.grey, marginTop: 10 }}>
              {page.type === 'FACTURE' ? 'À régler avant le ' : 'Valable jusqu’au '}
              <b style={{ color: C.ink }}>{dateSuisse(page.echeance)}</b>
            </div>
          )}
          {offre?.signataire && (
            <div style={{ fontSize: 13, color: C.grey, marginTop: 10 }}>
              {termes.accord('Accepté par', 'Acceptée par')}{' '}
              <b style={{ color: C.ink }}>{offre.signataire}</b>
              {offre.dateSignature ? ' le ' + dateSuisse(offre.dateSignature) : ''}
            </div>
          )}
          {offre?.refuseLe && (
            <div style={{ fontSize: 13, color: C.grey, marginTop: 10, lineHeight: 1.5 }}>
              {termes.feminin ? 'Vous avez refusé cette offre le' : 'Vous avez refusé ce devis le'}{' '}
              <b style={{ color: C.ink }}>{dateSuisse(offre.refuseLe)}</b>
              {offre.motifRefus ? ` · motif indiqué : « ${offre.motifRefus} »` : ''}
            </div>
          )}
          {offre?.acomptePourcentage != null && offre?.montantAcompte != null && (
            <div style={{ fontSize: 13, color: C.grey, marginTop: 6 }}>
              Acompte à la signature :{' '}
              {offre.acompteEnFrancs ? '' : quantiteLisible(offre.acomptePourcentage) + ' % — '}
              <b style={{ color: C.ink }}>
                {totaux?.devise || 'CHF'} {montantSuisse(offre.montantAcompte)}
              </b>
              {offre.factureAcompte ? ` (facture n° ${offre.factureAcompte})` : ''}
            </div>
          )}
        </section>
      )}

      {offre?.acceptable && (
        <AcceptationOffre
          jeton={jeton}
          page={page}
          onAcceptee={(relue) => {
            setPage(relue);
            setVientDAccepter(true);
            // Le remerciement est en haut de page : y ramener le client, quand
            // le navigateur le permet.
            try {
              window.scrollTo(0, 0);
            } catch {
              /* sans défilement : la page se lit quand même */
            }
          }}
        />
      )}
      {(offre?.refusable ?? offre?.acceptable) && (
        <RefusOffre
          jeton={jeton}
          page={page}
          onRefusee={(relue) => {
            setPage(relue);
            setVientDeRefuser(true);
            try {
              window.scrollTo(0, 0);
            } catch {
              /* sans défilement : la page se lit quand même */
            }
          }}
        />
      )}

      {livraison && (
        <section style={carte}>
          <div style={etiquette}>Livraison</div>
          {livraison.adresse && <div style={{ fontSize: 14 }}>📍 {livraison.adresse}</div>}
          <div style={{ fontSize: 13, color: C.grey, marginTop: 6, lineHeight: 1.6 }}>
            {livraison.livreLe
              ? 'Livrée le ' + dateSuisse(livraison.livreLe)
              : livraison.prevue
                ? 'Prévue le ' + dateSuisse(livraison.prevue)
                : ''}
            {livraison.recuPar ? ' · réceptionnée par ' + livraison.recuPar : ''}
          </div>
        </section>
      )}

      {chantier && <SuiviChantier chantier={chantier} />}

      {(page.type === 'DEVIS' || page.type === 'FACTURE') && (
        <FeuilleDocument jeton={jeton} onPret={setFeuillePrete}>
          {/* Les articles, puis le QR-facture à scanner, puis les photos. */}
          {paiement?.qr && blocPaiement}
        </FeuilleDocument>
      )}

      {!estChantier && !estCommande && !feuillePrete && (
        <section style={carte}>
          <div style={etiquette}>{page.type === 'LIVRAISON' ? 'Marchandise' : 'Détail'}</div>
          {(page.lignes || []).map((l, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 0',
                borderTop: i ? '1px solid ' + C.border : 'none',
                fontSize: 14,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{l.designation}</div>
                {l.detail && (
                  <div
                    style={{
                      fontSize: 13,
                      color: C.ink,
                      marginTop: 3,
                      lineHeight: 1.45,
                      whiteSpace: 'pre-line',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {l.detail}
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                  {quantiteLisible(l.quantite)} {l.unite || ''}
                  {l.prixUnitaire != null ? ' × ' + montantSuisse(l.prixUnitaire) : ''}
                  {l.remise > 0 ? ' − ' + quantiteLisible(l.remise) + ' %' : ''}
                </div>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                {l.etat ? (
                  <Etat statut={l.etat} />
                ) : (
                  <span style={{ fontWeight: 700 }}>{montantSuisse(l.montant)}</span>
                )}
              </div>
            </div>
          ))}
          {totaux && (
            <div style={{ borderTop: '2px solid ' + C.ink, marginTop: 6, paddingTop: 10 }}>
              {[
                ['Total hors taxe', totaux.horsTaxe],
                ['TVA', totaux.tva],
              ].map(([nom, valeur]) => (
                <div
                  key={nom}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                    color: C.grey,
                    padding: '3px 0',
                  }}
                >
                  <span>{nom}</span>
                  <span>{montantSuisse(valeur)}</span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 18,
                  fontWeight: 900,
                  paddingTop: 8,
                }}
              >
                <span>Total</span>
                <span>
                  {totaux.devise} {montantSuisse(totaux.ttc)}
                </span>
              </div>
              {/* L'acompte et le solde, comme au bas du PDF. */}
              {offre?.acomptePourcentage != null && offre?.montantAcompte != null && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.amber,
                      paddingTop: 8,
                    }}
                  >
                    <span>
                      {/* Écrit en francs, l'acompte se dit en francs. */}
                      {offre.acompteEnFrancs
                        ? 'Acompte à la signature'
                        : `Acompte ${quantiteLisible(offre.acomptePourcentage)} %`}
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {totaux.devise} {montantSuisse(offre.montantAcompte)}
                    </span>
                  </div>
                  {totaux.ttc != null && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                        fontSize: 14,
                        paddingTop: 4,
                      }}
                    >
                      <span>
                        {offre.acompteEnFrancs
                          ? 'Solde à la fin des travaux'
                          : `Solde (${quantiteLisible(100 - Number(offre.acomptePourcentage))} %) à la fin`}
                      </span>
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {totaux.devise}{' '}
                        {montantSuisse(Number(totaux.ttc) - Number(offre.montantAcompte))}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* Sans le document affiché, le bulletin vient après le détail. Avec
          lui, il se pose entre le document et les photos (ci-dessus) — ou
          pas du tout hors de Suisse : la feuille porte déjà les coordonnées
          du virement. */}
      {paiement && !feuillePrete && blocPaiement}

      {page.pdf && (
        // Le PDF est une feuille A4 : sur un téléphone, il s'affiche en tout
        // petit. Tout son contenu est ci-dessus, à la taille de l'écran ; le
        // PDF reste là pour l'imprimer ou le garder.
        <p
          style={{
            fontSize: 13,
            color: C.grey,
            textAlign: 'center',
            lineHeight: 1.5,
            margin: '0 0 8px',
          }}
        >
          Tout le document est affiché ci-dessus, à la taille de votre écran.
        </p>
      )}
      {page.pdf && (
        <a
          href={pagesPubliquesApi.adresseDuDocument(jeton)}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'block',
            textAlign: 'center',
            background: C.navy,
            color: '#fff',
            fontWeight: 800,
            fontSize: 15,
            borderRadius: 12,
            padding: '14px 16px',
            textDecoration: 'none',
            marginBottom: 14,
          }}
        >
          📄 Télécharger le PDF (A4, pour imprimer)
        </a>
      )}

      {/* Les attestations que l'entreprise a jointes à cette offre (RC,
          registre du commerce…) : chacune s'ouvre par le même lien. */}
      {estDevis && page.documents?.length > 0 && <DocumentsEntreprise documents={page.documents} />}

      {(page.type === 'DEVIS' || page.type === 'FACTURE') && (
        <Transmettre key={visiteur?.nom || ''} jeton={jeton} votreNom={visiteur?.nom} />
      )}

      <footer
        style={{ fontSize: 12, color: C.muted, textAlign: 'center', lineHeight: 1.6, padding: 8 }}
      >
        Une question ? {societe?.nom}
        {societe?.telephone ? ' · ' + societe.telephone : ''}
        {societe?.email ? ' · ' + societe.email : ''}
      </footer>
    </Cadre>
  );
}
